// ============================================
// Operations View
// Revenue by stream horizontal bars + recent activity feed
// ============================================

import { useMemo } from 'react';
import {
  CreditCard,
  Loader,
  Truck,
  CheckCircle2,
  Package,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import type { FinancialMetrics } from './useFinancialData';
import type { Order, OrderStatus } from '../../../types/order';

interface OperationsViewProps {
  metrics: FinancialMetrics;
  orders: Order[];
}

// --- Status styling ---

const STATUS_CONFIG: Record<
  OrderStatus,
  { colorClass: string; bgClass: string; icon: typeof Package }
> = {
  pending: { colorClass: 'text-text-muted', bgClass: 'bg-text-muted/20', icon: Clock },
  paid: { colorClass: 'text-gold', bgClass: 'bg-gold/20', icon: CreditCard },
  processing: { colorClass: 'text-suboptimal', bgClass: 'bg-suboptimal/20', icon: Loader },
  shipped: { colorClass: 'text-optimal', bgClass: 'bg-optimal/20', icon: Truck },
  delivered: { colorClass: 'text-text-secondary', bgClass: 'bg-text-secondary/20', icon: CheckCircle2 },
  cancelled: { colorClass: 'text-critical', bgClass: 'bg-critical/20', icon: XCircle },
};

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return `${Math.floor(diffDay / 30)}mo ago`;
}

export function OperationsView({ metrics, orders }: OperationsViewProps) {
  // Revenue by bundle (used as "revenue streams")
  const bundleData = metrics.revenueByBundle;
  const maxBundleRevenue = useMemo(
    () => Math.max(...bundleData.map((b) => b.revenueCents), 1),
    [bundleData],
  );

  // Recent activity: last 10 orders sorted by createdAt desc
  const recentOrders = useMemo(
    () =>
      [...orders]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, 10),
    [orders],
  );

  return (
    <div className="space-y-6">
      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Revenue by Stream */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
        >
          <h3 className="text-lg font-display text-text-primary mb-4">
            Revenue by Stream
          </h3>
          {bundleData.length === 0 ? (
            <p className="text-text-muted text-base">No revenue data yet.</p>
          ) : (
            <div className="space-y-3">
              {bundleData.map((bundle) => {
                const pct = (bundle.revenueCents / maxBundleRevenue) * 100;
                return (
                  <div key={bundle.bundleName}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-base text-text-secondary truncate mr-3">
                        {bundle.bundleName}
                      </span>
                      <span className="text-base font-medium text-text-primary whitespace-nowrap">
                        {formatCurrency(bundle.revenueCents)}
                      </span>
                    </div>
                    <div className="w-full bg-bg-elevated rounded-full h-3">
                      <div
                        className="bg-gold rounded-full h-3 transition-all duration-500"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-sm text-text-muted">
                        {bundle.orderCount} order{bundle.orderCount !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm text-text-muted">
                        {bundle.pctOfTotal}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT: Recent Activity */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
          style={{ animationDelay: '0.1s' }}
        >
          <h3 className="text-lg font-display text-text-primary mb-4">
            Recent Activity
          </h3>
          {recentOrders.length === 0 ? (
            <p className="text-text-muted text-base">No recent orders.</p>
          ) : (
            <ul className="space-y-2">
              {recentOrders.map((order) => {
                const cfg = STATUS_CONFIG[order.status];
                const Icon = cfg.icon;
                return (
                  <li
                    key={order.id}
                    className="flex items-start gap-3 py-2 border-b border-bg-tertiary last:border-0"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg ${cfg.bgClass} flex items-center justify-center flex-shrink-0 mt-0.5`}
                    >
                      <Icon size={16} className={cfg.colorClass} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base text-text-primary truncate">
                        <span className="font-medium">{order.id.slice(0, 8)}</span>
                        {order.bundleName && (
                          <>
                            <span className="text-text-muted mx-1.5">&mdash;</span>
                            <span className="text-text-secondary">{order.bundleName}</span>
                          </>
                        )}
                        <span className="text-text-muted mx-1.5">&mdash;</span>
                        <span className="font-medium">
                          {formatCurrency(order.totalInCents)}
                        </span>
                      </p>
                      <p className="text-sm text-text-muted">
                        {order.status} &middot; {relativeTime(order.createdAt)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* BOTTOM: Alerts */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
        style={{ animationDelay: '0.2s' }}
      >
        <h3 className="text-lg font-display text-text-primary mb-3">Alerts</h3>
        <div className="flex items-center gap-3 text-text-muted">
          <AlertTriangle size={18} />
          <p className="text-base">No alerts at this time.</p>
        </div>
      </div>
    </div>
  );
}
